import { useCallback } from 'react';
import { callDeepLink, compareWithAppVersion, isBrowser, login } from '@croquiscom-pvt/web2app';
import { alert, LogoZigzagSymbolRounded } from '@croquiscom-pvt/zds';
import { useRouter } from 'next/router';

import { useZigzagRouter } from '@common/app-manager';
import { getConfig } from '@common/config';
import { isMobile as useIsMobile } from '@common/device-manager';
import { DeskTopLandingPopup, HOME_LANDING_LINK, MobileLandingPopup, usePopupDialog } from '@common/overlay-manager';
import { isPreview, Router, useCatalogProductIdParams, useIsAuthenticated, usePdpConfig } from '@domains/pdp-common';

import { PARENT_IDS } from '../constants/inquiry';
import { PdpCatalogFulfillmentType } from '../graphql/contact-info_types.2';
import { useInquiryUBL } from '../log/useInquiryUBL';
import { useContactInfo } from '../queries/useContactInfoQuery';

const config = getConfig();

export const useInquiryService = () => {
  const zigzagRouter = useZigzagRouter();
  const { open } = usePopupDialog();
  const { isZigzagApp } = usePdpConfig();
  const { asPath } = useRouter();
  const _isPreview = isPreview(asPath);
  const isMobile = useIsMobile();
  const param = useCatalogProductIdParams();
  const {
    shop: { parent_id },
    product: { id, fulfillment_type, external_product_url },
  } = useContactInfo(param);
  const { isLoggedIn } = useIsAuthenticated();
  const { subtabInquiryTopClickUBL, subtabInquiryBottomClickUBL } = useInquiryUBL();

  const isZigzin = fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN;
  const isNotParentShop = !PARENT_IDS.includes(parent_id ?? '');
  const showDivider = isNotParentShop || isZigzin;
  const is_supported_version = isBrowser() && compareWithAppVersion('8.35.0');

  const inquiry_path_message = is_supported_version ? '마이페이지 > 1:1 문의' : '마이페이지 > 문의 내역';

  const question_url = is_supported_version
    ? `/checkout/inquiries/product/new?product_id=${id}&from=pdp`
    : `/order-item-inquiries/qna/new?product_id=${id}`;

  const history_url = is_supported_version ? '/checkout/inquiries/one-on-one?tab=HISTORY' : '/order-item-inquiries/qna';

  const addQuestion = () => {
    if (!isZigzagApp) {
      subtabInquiryTopClickUBL();

      alert({
        illust: <LogoZigzagSymbolRounded />,
        title: '해당 기능은\n앱에서 사용 가능해요!',
        description: '지그재그 앱에서 확인해보세요.',
        confirmText: isMobile ? '앱에서 보기' : '확인',
        onConfirm: () => {
          isMobile ? window.open(HOME_LANDING_LINK.KR, '_blank') : undefined;
        },
      });
      return;
    }
    if (!isLoggedIn) {
      login();
      return;
    }
    Router.push(question_url, { title: '상품 문의하기' });
  };

  const moveToInquiryHistory = () => {
    isLoggedIn ? zigzagRouter.push(history_url, { title: '문의 내역' }) : login();
  };

  const moveToMetaPdp = useCallback(() => {
    if (!external_product_url) {
      return;
    }
    if (!isZigzagApp) {
      window.open(external_product_url);
      return;
    }
    callDeepLink(external_product_url)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveToShippingCSChat = () => {
    if (!isZigzagApp) {
      subtabInquiryBottomClickUBL();

      return open(isMobile ? MobileLandingPopup() : DeskTopLandingPopup());
    }

    if (is_supported_version) {
      callDeepLink('/zendesk_browser')({
        url: encodeURIComponent(`${config.zendesk_url}?page=help`),
        title: '문의하기',
      });
    } else {
      callDeepLink('/contact/chat')();
    }
  };

  return {
    disabled: _isPreview,
    isZigzin,
    isZigzagApp,
    isLoggedIn,
    isNotParentShop,
    showDivider,
    inquiry_path_message,

    moveToInquiryHistory,
    moveToMetaPdp,
    moveToShippingCSChat,
    addQuestion,
  };
};
